
import { ApiResponse, handleApiCall, setSession } from './api';
import { SHA512 } from 'crypto-js';
import Timezone from 'timezone-enum';
import { ChallengeState, RecipientPage, SignInOutcome } from 'src/interfaces/admin';

interface AuthPayload {
    token?: string;
    challenge?: string;
    state?: ChallengeState;
}

function toOutcome(response: ApiResponse<AuthPayload>): SignInOutcome {
    if (!response.success) {
        return { kind: 'error', message: response.message };
    }
    if (response.data.token) {
        setSession(response.data.token);
        return { kind: 'session', token: response.data.token };
    }
    return { kind: 'challenge', challenge: response.data.challenge, state: response.data.state };
}

export async function signIn(user: string, rawPassword: string, browser: string): Promise<SignInOutcome> {

    const password = SHA512(rawPassword).toString();
    return toOutcome(await handleApiCall<AuthPayload>('post', 'v2/public/auth', {
        body: { username: user, password, browser },
    }));
}

export async function fetchLoginRecipients(challenge: string, search: string, page = 0, limit = 10):
    Promise<ApiResponse<RecipientPage>> {
    return await handleApiCall<RecipientPage>('post', 'v2/public/auth/recipients', {
        body: { challenge, search, page, limit },
    });
}

export async function enrolLoginRecipient(challenge: string, userUid: string): Promise<SignInOutcome> {
    return toOutcome(await handleApiCall<AuthPayload>('post', 'v2/public/auth/enrol', {
        body: { challenge, user_uid: userUid },
    }));
}

export async function verifyLoginOtp(challenge: string, otp: string): Promise<SignInOutcome> {
    return toOutcome(await handleApiCall<AuthPayload>('post', 'v2/public/auth/otp', { body: { challenge, otp } }));
}

export async function resendLoginOtp(challenge: string): Promise<SignInOutcome> {
    return toOutcome(await handleApiCall<AuthPayload>('post', 'v2/public/auth/otp/resend', { body: { challenge } }));
}
export async function confirmPwordReset(token: string, pword?: string): Promise<ApiResponse<void>> {

    if (pword) {
        return await handleApiCall<void>('post', 'v2/public/confirm', {
            body: {
                token,
                new_password: SHA512(pword).toString()
            }
        });
    }
    else {
        return await handleApiCall<void>('post', 'v2/public/confirm', { body: { token } });
    }
}

export async function requestPwordReset(user: string): Promise<ApiResponse<void>> {

    return await handleApiCall<void>('post', 'v2/public/reset', {
        body: {
            username: user,
        }
    });
}

export async function signUp(name: string, first_name: string,
    last_name: string, username: string, lang: string, captcha?: string): Promise<ApiResponse<void>> {

    const locale = new Intl.Locale(navigator.language);
    const regionCode = locale.region || 'US';

    return await handleApiCall<void>('post', 'v2/public/register', {
        body: {
            username,
            name,
            first_name,
            last_name,
            lang,
            country: regionCode,
            timezone: Timezone[Intl.DateTimeFormat().resolvedOptions().timeZone],
            captcha,
        }
    })
}

export async function getPublicSettings(): Promise<ApiResponse<{ emailing_enabled: boolean, registration_enabled: boolean,
    warning_banner: {en: string; pt: string; es: string; fr: string}; }>> {

    return await handleApiCall('get', 'v2/public/settings');
}

export async function verify(token: string, pword?: string): Promise<ApiResponse<void>> {

    if (pword) {
        return await handleApiCall<void>('post', 'v2/public/verify', {
            body: {
                token,
                new_password: SHA512(pword).toString()
            }
        });
    }
    else {
        return await handleApiCall<void>('post', 'v2/public/verify', { body: { token } });
    }
}
